import { isMoneyColumn } from 'ds/Table/utils';

import Header from './Header';
import { CheckboxCell, MoneyCell } from '../Cells/CellPatterns';
import { getDefaultPaddingVariantForContentType } from '../constants';
import { Column } from '../types';
import Layout from '../../Layout';
import { BORDER_WIDTH } from '../../Table/constants';
import { ColumnWidthProps, Row, SortFilterProps } from '../../Table/types';

interface Props<T extends Row> extends SortFilterProps, ColumnWidthProps {
  columns: Column<T>[];
  style: React.CSSProperties;
  disableTableControlsHeader: boolean;
}

const HeaderRow = <T extends Row>({
  style,
  columns,
  sortState,
  setSortState,
  setColumnWidths,
  getColumnWidth
}: Props<T>) => {
  return (
    <Layout
      position="sticky"
      zIndex={30}
      direction="row"
      align="center"
      color="white"
      __style={{ ...style, top: '0px' }}
      borderBottom
      borderColor="gray-50"
    >
      {/** This element covers up tiny gap between header and table where a crack of text can appear. */}
      <Layout position="absolute" top={-BORDER_WIDTH} color="white" __style={{ height: BORDER_WIDTH }} width="100%" />
      {columns.map(
        (
          {
            header,
            key: columnKey,
            align,
            sortBy,
            backendSort,
            shrinkWidth,
            flexGrow,
            minWidth,
            maxWidth,
            width,
            Header: CustomHeader,
            Cell,
            paddingVariant,
            cellContentType,
            headerSelectAll
          },
          columnIndex
        ) => {
          const key = columnKey as string;
          const sortStateIndex = sortState.findIndex(sort => sort.key === key);
          const sort = sortState[sortStateIndex];
          const isCheckboxCell = Cell && (Cell as React.ComponentType<unknown>) === CheckboxCell;
          const defaultPaddingVariant = getDefaultPaddingVariantForContentType(cellContentType);
          const resolvedPaddingVariant = paddingVariant ?? (isCheckboxCell ? 'compactRight' : defaultPaddingVariant);

          return (
            <Header
              key={key}
              columnKey={key}
              value={header}
              setSortState={direction => {
                const updatedSortState = direction === null ? [] : [{ key, direction }];

                setSortState(updatedSortState);
              }}
              setColumnWidths={setColumnWidths}
              getColumnWidth={getColumnWidth}
              flexGrow={flexGrow}
              align={align}
              CustomHeader={CustomHeader}
              minWidth={minWidth}
              maxWidth={maxWidth}
              width={width}
              shrinkWidth={!!shrinkWidth}
              activeSortDirection={sort ? sort.direction : undefined}
              sortable={!!sortBy || !!backendSort}
              isMoney={(Cell && (Cell as React.ComponentType<unknown>) === MoneyCell) || isMoneyColumn(columnKey)}
              isFirstColumn={columnIndex === 0}
              paddingVariant={resolvedPaddingVariant}
              cellContentType={cellContentType}
              headerSelectAll={headerSelectAll}
            />
          );
        }
      )}
    </Layout>
  );
};

export default HeaderRow;
